import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Navigation from './components/Navigation';
import SignupForm from './components/SignupForm';
import PayPalConnectAccount from './components/PayPalConnectAccount';
import { SERVER_URL } from './config';

function AcceptPool() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, signup, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pool, setPool] = useState(null);
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryEmail, setBeneficiaryEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [needsAccount, setNeedsAccount] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [poolAccepted, setPoolAccepted] = useState(false);
  const [needsPayPal, setNeedsPayPal] = useState(false);

  useEffect(() => {
    // Fetch pool details
    fetch(`${SERVER_URL}/accept-pool/${token}`)
      .then(res => {
        if (!res.ok) {
          return res.json().then(errorData => {
            // Check if pool was already accepted
            if (errorData.detail && errorData.detail.includes('already been accepted')) {
              throw new Error('ALREADY_ACCEPTED');
            }
            throw new Error('Invalid or expired acceptance link');
          });
        }
        return res.json();
      })
      .then(data => {
        setPool(data);
        setBeneficiaryName(data.beneficiary_name);
        setBeneficiaryEmail(data.beneficiary_email);

        // Check if user needs to create an account
        // Need account if:
        // 1. Not authenticated at all, OR
        // 2. Authenticated with matching email but no password (was auto-created)
        if (!isAuthenticated) {
          setNeedsAccount(true);
          // Leave name field empty for user to fill in
          setBeneficiaryName('');
        } else if (user && user.email.toLowerCase() !== data.beneficiary_email.toLowerCase()) {
          // User is logged in but with different email - they need to use the correct account
          setError(
            `This gift pool is for ${data.beneficiary_email}. Please log out and sign in with that email address, or use the acceptance link while not logged in.`
          );
          setLoading(false);
          return;
        } else if (user && !user.has_password) {
          // User is logged in with matching email but doesn't have a password - they need to complete signup
          setNeedsAccount(true);
          // Pre-fill name from existing user record
          setBeneficiaryName(user.username);
        } else {
          // User is authenticated with matching email and has a password
          setNeedsAccount(false);
        }

        setLoading(false);
      })
      .catch(err => {
        // Show friendly message if pool was already accepted
        if (err.message === 'ALREADY_ACCEPTED') {
          setError('This gift pool has already been accepted');
        } else {
          setError(err.message);
        }
        setLoading(false);
      });
  }, [token, isAuthenticated, user]);

  const handleAccept = async () => {
    setLoading(true);
    setError('');

    try {
      // If user needs an account, create it first
      if (needsAccount) {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        if (!beneficiaryName.trim()) {
          throw new Error('Please provide your name');
        }
        await signup(beneficiaryName.trim(), beneficiaryEmail, password);
      }

      // Accept the pool
      const res = await fetch(`${SERVER_URL}/accept-pool/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          beneficiary_name: (user?.username || beneficiaryName).trim(),
          beneficiary_email: beneficiaryEmail,
          goal_amount: null,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to accept gift pool');
      }

      // Pool accepted — now check if PayPal needs to be connected
      const paypalStatusRes = await fetch(`${SERVER_URL}/paypal/onboard/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (paypalStatusRes.ok) {
        const paypalStatus = await paypalStatusRes.json();
        if (!paypalStatus.payments_receivable) {
          setPoolAccepted(true);
          setNeedsPayPal(true);
          setLoading(false);
          return;
        }
      }

      navigate(`/feed?accepted=true`);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this gift pool?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${SERVER_URL}/decline-pool/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: declineReason,
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to decline gift pool');
      }

      // Redirect to home with declined message
      navigate('/?declined=true');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Pool accepted — prompt for PayPal setup inline
  if (poolAccepted && needsPayPal) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-6 shadow sm:rounded-lg">
            <div className="text-center mb-6">
              <div className="text-3xl mb-2">🎉</div>
              <h2 className="text-xl font-bold text-gray-900">Gift pool accepted!</h2>
              <p className="text-sm text-gray-600 mt-1">
                One last step — connect your PayPal account so contributors can send you gifts. Click <strong>Connect PayPal</strong> below and follow the prompts. You can also do this later from <strong>Settings → Payments</strong>.
              </p>
            </div>
            <PayPalConnectAccount
              onSuccess={() => navigate('/feed?accepted=true')}
            />
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/feed?accepted=true')}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !pool) {
    return (
      <div className="max-w-2xl mx-auto mt-10 text-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (error && !pool) {
    // Show green success message if pool was already accepted
    const isAlreadyAccepted = error === 'This gift pool has already been accepted';

    return (
      <div className="max-w-2xl mx-auto mt-10">
        <div
          className={`${isAlreadyAccepted ? 'bg-green-100 border-green-400 text-green-700' : 'bg-red-100 border-red-400 text-red-700'} border p-4 rounded`}
        >
          {isAlreadyAccepted ? '✅ ' : ''}
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">💝 Gift Pool Created For You!</h1>
          <p className="text-gray-600">
            <Link
              to={`/profile/${pool.creator_id}`}
              className="font-semibold text-indigo-600 hover:text-indigo-700 hover:underline transition-colors"
            >
              {pool.creator_name}
            </Link>{' '}
            started a gift pool for{' '}
            <span className="font-semibold text-gray-900">{pool.beneficiary_name}</span>
          </p>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">{pool.title}</h2>
          <p className="text-gray-700 mb-4 whitespace-pre-wrap">{pool.description}</p>

          <div className="text-sm">
            <span className="text-gray-600">Category:</span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700 ml-2">
              {pool.category}
            </span>
          </div>
        </div>

        {needsAccount && (
          <div className="mb-6">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-indigo-800 font-medium mb-2">🔐 Create Your Account</p>
              <p className="text-xs text-indigo-700">
                Set up your account to accept this gift pool and start receiving gifts
              </p>
            </div>

            <SignupForm
              name={beneficiaryName}
              email={beneficiaryEmail}
              password={password}
              confirmPassword={confirmPassword}
              onNameChange={e => setBeneficiaryName(e.target.value)}
              onEmailChange={e => setBeneficiaryEmail(e.target.value)}
              onPasswordChange={e => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              onConfirmPasswordChange={e => {
                setConfirmPassword(e.target.value);
                if (error) setError('');
              }}
              error={error}
              loading={false}
              onSubmit={e => {
                e.preventDefault();
                handleAccept();
              }}
              submitButtonText="Create Account & Accept"
              showEmailField={true}
              emailReadOnly={true}
              nameLabel="Username"
              namePlaceholder="johndoe"
            />

            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link
                  to={`/login?redirect=/accept-pool/${token}`}
                  className="text-indigo-600 hover:text-indigo-700 font-medium hover:underline"
                >
                  Log in instead
                </Link>
              </p>
            </div>
          </div>
        )}

        {!needsAccount && (
          <div className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
                <span className="block sm:inline">{error}</span>
                {error.includes('Settings') && (
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        sessionStorage.setItem('paypal_onboarding_context', 'pool_acceptance');
                        sessionStorage.setItem('paypal_onboarding_data', JSON.stringify({ token }));
                        navigate('/settings');
                      }}
                      className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded hover:bg-indigo-700 transition-colors"
                    >
                      Go to Settings → Payments
                    </button>
                  </div>
                )}
              </div>
            )}

            {!showDeclineForm ? (
              <div className="flex gap-4">
                <button
                  onClick={handleAccept}
                  disabled={loading}
                  className="flex-1 bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:bg-gray-400"
                >
                  ✓ Accept Gift Pool
                </button>
                <button
                  onClick={() => setShowDeclineForm(true)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Decline
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for declining (optional)
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={e => setDeclineReason(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    rows="3"
                    placeholder="Let the creator know why..."
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleDecline}
                    className="flex-1 bg-red-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors"
                  >
                    Confirm Decline
                  </button>
                  <button
                    onClick={() => setShowDeclineForm(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {needsAccount && (
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowDeclineForm(true)}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Decline
            </button>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600 text-center">
            {pool.visibility === 'everyone'
              ? 'Once you accept, this gift pool will appear in the public feed and people can start gifting.'
              : 'Once you accept, this gift pool will appear in your network feed and your followers can start gifting.'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default AcceptPool;
